<template>
    <form-modal
        :open="isEditModalOpen"
        :heading="$t('modals.user.edit')"
        entity="user"
        mode="edit"
        :submit-label="$t('modals.user.save')"
        :fields="fields"
        :values="formValues"
        :info="info"
        separated
        save-url="/edit-user.php"
        :build-payload="buildPayload"
        @update:open="$emit('update:isEditModalOpen', $event)"
        @opened="fetchUserDetails"
        @saved="onSaved"
    />
</template>

<script>
import { ref, computed, inject } from 'vue';
import apiClient from '@/api/apiClient';
import FormModal from '@/components/common/FormModal.vue';
import { t, currentLocale, setLocale, LOCALES } from '@/i18n';

export default {
    components: {
        FormModal,
    },
    props: {
        isEditModalOpen: {
            type: Boolean,
            required: true,
        },
        selectedUser: {
            type: Object,
            default: () => ({ user_id: null, name: null, username: null, email: null, password: null }),
        },
    },
    emits: ['update:isEditModalOpen', 'user-saved'],
    setup(props, { emit }) {
        const eventBus = inject('eventBus');
        const user = ref({});
        // The form starts empty: filled fields replace the current value, empty ones are left as they are
        const formValues = ref({ user_id: null });

        const info = computed(() => [
            { label: t('fields.id'), value: user.value.user_id },
            { label: t('fields.name'), value: user.value.name },
            { label: t('fields.username'), value: user.value.username },
            { label: t('fields.email'), value: user.value.email },
        ]);

        const fields = computed(() => [
            { key: 'newName', label: '', type: 'text', placeholder: t('fields.newName') },
            { key: 'newUsername', label: '', type: 'text', placeholder: t('fields.newUsername') },
            { key: 'newEmail', label: '', type: 'email', placeholder: t('fields.newEmail') },
            { key: 'newPassword', label: '', type: 'password', placeholder: t('fields.newPassword') },
            { key: 'locale', label: t('fields.language'), type: 'select', options: LOCALES.map((option) => ({ value: option.code, label: option.name })) },
        ]);

        const buildPayload = (form) => ({
            userId: user.value.user_id,
            userName: form.newName || '',
            userUsername: form.newUsername || '',
            userEmail: form.newEmail || '',
            userPassword: form.newPassword || '',
            userLocale: form.locale || '',
        });

        const fetchUserDetails = () => {
            user.value = {};
            apiClient.get('/get-user-details.php', { params: { userId: props.selectedUser.user_id } })
                .then((response) => {
                    user.value = response.data;
                    // the other fields start empty (only what is filled in changes); the language starts as the account's
                    formValues.value = { locale: response.data.locale || currentLocale() };
                })
                .catch((error) => {
                    console.error('Error fetching user details:', error);
                });
        };

        const onSaved = (data, payload) => {
            emit('user-saved', payload);

            // editing your own account: its language becomes the language of the app
            if (payload.userLocale && String(payload.userId) === localStorage.getItem('userId')) {
                setLocale(payload.userLocale);
            }

            // Let other components (navbar, setup card) refresh the displayed user
            if (eventBus) {
                eventBus.$emit('user-details-updated', payload);
            } else {
                console.error('Event bus not found.');
            }
        };

        return { fields, formValues, info, buildPayload, fetchUserDetails, onSaved };
    },
};
</script>
